import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAppStore } from '../../stores/appStore';
import { toFriendlyErrorMessage } from '../../utils/friendlyErrors';
import { buildApiUrl, getServerOrigin } from '../../utils/serverOrigin';

const INPUT_CLASSES =
  'w-full bg-card border border-input rounded-lg text-foreground text-sm px-3 py-2 focus:border-ring focus:ring-1 focus:ring-ring outline-none';

const PRIMARY_BUTTON_CLASSES =
  'rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover disabled:opacity-70 disabled:cursor-not-allowed';

const SECONDARY_BUTTON_CLASSES =
  'rounded-lg border border-input px-3 py-2 text-sm text-secondary-foreground hover:bg-muted transition-colors';

interface Invite {
  id: string;
  token: string;
  display_name: string;
  gateway_url: string;
  gateway_token: string;
  max_uses: number;
  use_count: number;
  expires_at: number | null;
  created_at: number;
  allowed_agents?: string[];
  role?: 'builder' | 'member';
}

type InviteStatus = 'active' | 'used' | 'expired';

function getInviteStatus(invite: Invite): InviteStatus {
  if (invite.use_count >= invite.max_uses) {
    return 'used';
  }
  if (invite.expires_at !== null && Date.now() > invite.expires_at) {
    return 'expired';
  }
  return 'active';
}

function StatusBadge({ status }: { status: InviteStatus }) {
  const classes = {
    active: 'bg-primary/20 text-accent-foreground',
    used: 'bg-muted text-muted-foreground',
    expired: 'bg-destructive/20 text-destructive-foreground',
  };

  const labels = {
    active: 'Active',
    used: 'Used',
    expired: 'Expired',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${classes[status]}`}>
      {labels[status]}
    </span>
  );
}

function SettingSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-0.5 text-sm font-semibold text-foreground">{title}</h4>
      <p className="mb-3 text-xs text-muted-foreground">{description}</p>
      {children}
    </div>
  );
}

export default function FamilyTab() {
  const gatewayUrl = useAppStore((s) => s.gatewayUrl);
  const gatewayToken = useAppStore((s) => s.gatewayToken);
  const agents = useAppStore((s) => s.agents);

  // Filter to agents with identity (same logic as SessionSidebar)
  const pickableAgents = agents.filter((agent) => agent.identity != null);

  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create invite form state
  const [inviteRole, setInviteRole] = useState<'builder' | 'member'>('member');
  const [displayName, setDisplayName] = useState('');
  const [expiryHours, setExpiryHours] = useState(72);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  // Copy state - track which token was copied
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(buildApiUrl('/api/invites/list', gatewayUrl), {
        headers: {
          Authorization: `Bearer ${gatewayToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch invites: ${res.status}`);
      }

      const data = await res.json();
      setInvites(data.invites || []);
    } catch (err) {
      console.error('[FamilyTab] Failed to fetch invites:', err);
      setError(toFriendlyErrorMessage(err, 'invite-list'));
    } finally {
      setLoading(false);
    }
  }, [gatewayToken, gatewayUrl]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) {
      setCreateError('Display name is required');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      setCreatedUrl(null);

      const res = await fetch(buildApiUrl('/api/invites/create', gatewayUrl), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${gatewayToken}`,
        },
        body: JSON.stringify({
          display_name: displayName.trim(),
          gateway_url: gatewayUrl,
          gateway_token: gatewayToken,
          expires_in_hours: expiryHours,
          role: inviteRole,
          allowed_agents: inviteRole === 'member' && selectedAgentIds.length > 0
            ? selectedAgentIds : undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to create invite: ${res.status}`);
      }

      const data = await res.json();

      // Build invite URL client-side with all params embedded
      const token = data.token ?? data.invite_token ?? '';
      const inviteParams = new URLSearchParams();
      inviteParams.set('gatewayUrl', gatewayUrl);
      inviteParams.set('gatewayToken', gatewayToken);
      inviteParams.set('displayName', displayName.trim());
      inviteParams.set('role', inviteRole);
      if (token) {
        inviteParams.set('inviteToken', token);
      }
      if (inviteRole === 'member' && selectedAgentIds.length > 0) {
        inviteParams.set('allowedAgents', selectedAgentIds.join(','));
      }
      const inviteUrl = new URL(`/?${inviteParams.toString()}`, getServerOrigin(gatewayUrl)).toString();
      setCreatedUrl(inviteUrl);

      setDisplayName('');
      setInviteRole('member');
      setSelectedAgentIds([]);

      // Refresh the list
      await fetchInvites();
    } catch (err) {
      console.error('[FamilyTab] Failed to create invite:', err);
      setCreateError(toFriendlyErrorMessage(err, 'invite-create'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (token: string) => {
    if (!confirm('Are you sure you want to revoke this invite?')) {
      return;
    }

    try {
      const res = await fetch(buildApiUrl(`/api/invites/${token}`, gatewayUrl), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${gatewayToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to revoke invite: ${res.status}`);
      }

      // Refresh the list
      await fetchInvites();
    } catch (err) {
      console.error('[FamilyTab] Failed to revoke invite:', err);
      alert(toFriendlyErrorMessage(err, 'invite-revoke'));
    }
  };

  const copyToClipboard = async (token: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      console.error('[FamilyTab] Failed to copy invite link:', error);
      // Fallback for older browsers
      alert(toFriendlyErrorMessage(error, 'clipboard'));
    }
  };

  const truncateUrl = (token: string) => {
    return `${getServerOrigin(gatewayUrl)}/join?token=${token.slice(0, 6)}...`;
  };

  const getFullUrl = (token: string) => {
    return `${getServerOrigin(gatewayUrl)}/join?token=${token}`;
  };

  return (
    <div className="space-y-6">
      <SettingSection
        title="Team Members"
        description="Invite team members to connect to your workspace."
      >
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-muted-foreground" />
            Loading invites...
          </div>
        ) : error ? (
          <div className="text-sm text-destructive-foreground">{error}</div>
        ) : invites.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No invites yet. Create one below.
          </div>
        ) : (
          <div className="space-y-2">
            {invites.map((invite) => {
              const status = getInviteStatus(invite);
              const fullUrl = getFullUrl(invite.token);
              const isCopied = copiedToken === invite.token;

              return (
                <div
                  key={invite.id}
                  className="flex items-center justify-between gap-3 p-3 bg-card border border-input rounded-lg"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {invite.display_name}
                        </span>
                        <StatusBadge status={status} />
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            background: (invite.role ?? (invite.allowed_agents?.length ? 'member' : 'builder')) === 'builder'
                              ? 'color-mix(in srgb, var(--theme-primary, hsl(var(--primary))) 20%, transparent)'
                              : 'var(--theme-muted, hsl(var(--muted)))',
                            color: (invite.role ?? (invite.allowed_agents?.length ? 'member' : 'builder')) === 'builder'
                              ? 'var(--theme-primary, hsl(var(--accent-foreground)))'
                              : 'var(--theme-muted-foreground, hsl(var(--muted-foreground)))',
                          }}
                        >
                          {(invite.role ?? (invite.allowed_agents?.length ? 'member' : 'builder')) === 'builder' ? 'Builder' : 'Member'}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-0.5">
                        {truncateUrl(invite.token)}
                      </div>
                      {invite.allowed_agents && invite.allowed_agents.length > 0 && (
                        <div className="text-xs text-muted-foreground/70 mt-0.5">
                          Agents: {invite.allowed_agents.length === 1
                            ? invite.allowed_agents[0]
                            : `${invite.allowed_agents.length} selected`}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => copyToClipboard(invite.token, fullUrl)}
                      className={SECONDARY_BUTTON_CLASSES}
                    >
                      {isCopied ? 'Copied!' : 'Copy Link'}
                    </button>

                    {status === 'active' && (
                      <button
                        type="button"
                        onClick={() => handleRevoke(invite.token)}
                        className="rounded-lg border border-destructive/50 px-3 py-2 text-sm text-destructive-foreground hover:bg-destructive/10 transition-colors"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SettingSection>

      <div className="border-t border-border pt-6">
        <SettingSection
          title="Create Invite"
          description="Generate a new invite link for a team member."
        >
          <form onSubmit={handleCreateInvite} className="space-y-4">
            {/* Role Selector */}
            <div>
              <label className="mb-1.5 block text-xs text-muted-foreground">
                Role
              </label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { value: 'builder' as const, label: 'Builder', desc: 'Full access — admin, settings, all agents' },
                  { value: 'member' as const, label: 'Member', desc: 'Chat only — selected agents' },
                ] as const).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setInviteRole(option.value);
                      if (option.value === 'builder') setSelectedAgentIds([]);
                    }}
                    className="rounded-lg border p-3 text-left transition-colors"
                    style={{
                      borderColor: inviteRole === option.value
                        ? 'var(--theme-primary, hsl(var(--primary)))'
                        : 'var(--theme-border, hsl(var(--input)))',
                      background: inviteRole === option.value
                        ? 'color-mix(in srgb, var(--theme-primary, hsl(var(--primary))) 10%, transparent)'
                        : 'transparent',
                    }}
                  >
                    <div className="text-sm font-medium" style={{
                      color: inviteRole === option.value
                        ? 'var(--theme-primary, hsl(var(--primary)))'
                        : 'var(--theme-foreground, hsl(var(--foreground)))',
                    }}>
                      {option.label}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-foreground, hsl(var(--muted-foreground)))' }}>
                      {option.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="invite-display-name" className="mb-1 block text-xs text-muted-foreground">
                Display Name
              </label>
              <input
                id="invite-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={INPUT_CLASSES}
                placeholder="e.g. Alex, Sarah, Designer"
              />
            </div>

            {/* Agent Scope Checkboxes — only for member role */}
            {inviteRole === 'member' && pickableAgents.length > 0 && (
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Visible Agents
                </label>
                <p className="mb-2 text-xs text-muted-foreground/70">
                  Leave all unchecked to allow access to all agents
                </p>
                <div className="space-y-1.5">
                  {pickableAgents.map((agent) => (
                    <label key={agent.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedAgentIds.includes(agent.id)}
                        onChange={(e) => {
                          setSelectedAgentIds((prev) =>
                            e.target.checked
                              ? [...prev, agent.id]
                              : prev.filter((id) => id !== agent.id)
                          );
                        }}
                        className="rounded border-input"
                      />
                      <span className="text-sm text-foreground">
                        {agent.identity?.emoji ? `${agent.identity.emoji} ` : ''}
                        {agent.identity?.name ?? agent.id}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="max-w-44">
              <label htmlFor="invite-expiry" className="mb-1 block text-xs text-muted-foreground">
                Expires in (hours)
              </label>
              <input
                id="invite-expiry"
                type="number"
                min={1}
                max={720}
                value={expiryHours}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!Number.isNaN(val)) {
                    setExpiryHours(Math.min(720, Math.max(1, Math.trunc(val))));
                  }
                }}
                className={INPUT_CLASSES}
              />
            </div>

            <button
              type="submit"
              disabled={creating || !displayName.trim()}
              className={PRIMARY_BUTTON_CLASSES}
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Creating...
                </span>
              ) : (
                'Create Invite'
              )}
            </button>

            {createError && (
              <p className="text-xs text-destructive-foreground">{createError}</p>
            )}

            {createdUrl && (
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Invite link created:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-foreground bg-card px-2 py-1 rounded border border-input truncate">
                    {createdUrl}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard('new', createdUrl)}
                    className={SECONDARY_BUTTON_CLASSES}
                  >
                    {copiedToken === 'new' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-accent-foreground bg-primary/10 px-2 py-1.5 rounded-md">
                  💡 Tip: Test invite links in a private/incognito browser window
                </p>
              </div>
            )}
          </form>
        </SettingSection>
      </div>
    </div>
  );
}
